import { FC, useMemo } from 'react'
import styles from 'components/features/pfm/Navigation/NavigationItem.styl'
import cn from 'classnames'
import { MenuNavPageType } from 'interfaces/pages'
import SvgIcon from 'components/atoms/SvgIcon'
import { PageDefinition } from 'constants/pages'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import { NavigationLink } from 'components/atoms/navigation/NavigationLink'
import { AnalyticsClick } from 'components/features/pfm/AnalyticsClick/AnalyticsClick'
import { NavigationRenderMode } from 'interfaces/navigation'
import { Counter, CounterProps } from 'components/features/pfm/Counter/Counter'
import { NAVIGATION_COUNTERS_MAPPING } from 'interfaces/sidebar'
import { formatUSCurrency } from 'helpers/formatHelpers'
import { PremiumOnlyIconNavigationLink } from 'components/features/pfm/PremiumOnlyIconNavigationLink/PremiumOnlyIconNavigationLink'

export type NavigationItemProps =
  & PageDefinition<MenuNavPageType>
  & {
    mode: NavigationRenderMode
    isActive?: boolean
    isNested?: boolean
  }

export const NavigationItem: FC<NavigationItemProps> = (props) => {
  const {
    title,
    name,
    mode,
    isActive,
    isNested,
  } = props
  const preferences = useStoreSelector((state) => state.data.preferences)
  const rootClassName = cn(styles.navigationItem, {
    [styles.active]: isActive,
    [styles.nested]: isNested,
    [styles.compact]: mode === 'compact',
    [styles.serifNav]: preferences.mainNavTextStyle === 'serif',
  })

  const icon = require(`icons/${ name }.svg`).default

  const counters = useStoreSelector((state) => state.app.sidebar.counters.actual)
  const counterChild = useMemo(() => {
    const counterKey = NAVIGATION_COUNTERS_MAPPING[name]
    const value = counterKey ? counters[counterKey] : undefined
    if (!value) {
      return null
    }

    const wrapperClassName = cn(styles.counter, (styles as any)[name])
    const counterProps: CounterProps = name === 'rewards'
      ? {
        count: formatUSCurrency(value, 'penny'),
        theme: 'golden',
        shape: 'square',
      }
      : {
        count: `${ value }`,
        theme: 'green',
        shape: 'round',
      }

    return (
      <div className={ wrapperClassName }>
        <Counter { ...counterProps } />
      </div>
    )

  }, [counters, name])

  const Analytics: FC = ({ children }) => (
    <AnalyticsClick
      category='site_controls-navigation_cta'
      action='click'
      label={ name }
      className={ styles.analytics }
    >
      { children }
    </AnalyticsClick>
  )

  const LinkWrapper = ({ children }: any) => {
    return (
      <Analytics>
        <NavigationLink
          className={ rootClassName }
          to={ name }
        >
          <div className={ styles.innerWrapper }>
            { children }
          </div>
        </NavigationLink>
      </Analytics>
    )
  }

  const lockChild = useMemo(() => (
    <div className={ styles.lock }>
      <PremiumOnlyIconNavigationLink to={ name } />
    </div>
  ), [name])

  return (
    <LinkWrapper>
      <div className={ styles.icon }>
        { icon && (
          <SvgIcon src={ icon } />
        )}
      </div>
      <span className={ styles.text } data-qa-type='navigation-link'>
        { title }
      </span>
      { counterChild }
      { lockChild }
    </LinkWrapper>
  )
}
